/**
 * Session-expiry detection.
 *
 * There is no refresh token anywhere in this system (see PR #79): the Cognito
 * bearer lives in memory and simply stops being accepted when it expires. Until
 * now a 401 mid-session surfaced as a raw "HTTP 401" string in whichever
 * component happened to make the call — reads kept rendering from the react-query
 * cache while every write failed, which reads as "the app is broken", not "sign
 * in again" (reported from production on 2026-08-09).
 *
 * This module is the one place that turns that 401 into something a person can
 * act on: any authenticated /api/v1 call that comes back 401 marks the session
 * expired, and the app shell shows ONE notice with a "Sign in" action. Screens
 * that would otherwise show their own "could not load — try again" error for
 * the same 401 read `isSessionExpired()` and stay quiet (spec §5: suppress
 * secondary unauthenticated retry errors).
 *
 * The /api/v1/auth endpoints must NEVER be routed through here — a wrong
 * password or MFA code is also a 401 and is not an expired session.
 */

export const SESSION_EXPIRED_MESSAGE = 'Session expired. Sign in to continue.';

let expired = false;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Latch the expired state and tell every subscriber. Idempotent — the burst of
 *  parallel queries that all 401 together must produce one notice, not many. */
export function markSessionExpired(): void {
  if (expired) return;
  expired = true;
  notify();
}

/**
 * Release the latch. Called by the notice's own "Sign in" action once the
 * expired session has been signed out: the app is navigated to sign-in WITHOUT
 * a page reload (so nothing held in memory is thrown away by the browser), and
 * the module state survives that navigation — left latched, the notice would
 * reappear the moment the adviser signed back in.
 */
export function clearSessionExpired(): void {
  if (!expired) return;
  expired = false;
  notify();
}

export function isSessionExpired(): boolean {
  return expired;
}

/** Subscribe to the latch flipping; returns the unsubscribe. Shaped for
 *  React's useSyncExternalStore. */
export function subscribeSessionExpired(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Tests only: the latch is module state and vitest shares modules per file. */
export function resetSessionExpiryForTests(): void {
  expired = false;
  listeners.clear();
}

/**
 * Inspect an authenticated API response. On 401 this latches the notice and
 * returns the friendly error for the caller to throw in place of its generic
 * "HTTP 401" message; any other status returns null and the caller's own
 * error handling proceeds untouched.
 */
export function sessionExpiryError(res: { status: number }): Error | null {
  if (res.status !== 401) return null;
  markSessionExpired();
  return new Error(SESSION_EXPIRED_MESSAGE);
}
